import { isKornitSettingsPath } from "./kornitNav";
import { isBespokeThreadsDomain, isOmniCommerceDomain } from "../utils";

/** Riverr global Settings — URL prefix and helpers (sidebar swap + routing). */
export const SETTINGS_PREFIX = "/settings";

export function isSettingsPath(pathname) {
  if (!pathname) return false;
  // Kornit has its own dedicated sidebar (Integrations → Configure).
  if (isKornitSettingsPath(pathname)) return false;
  return (
    pathname === SETTINGS_PREFIX || pathname.startsWith(`${SETTINGS_PREFIX}/`)
  );
}

export const RIVERR_LAST_NON_SETTINGS_PATH_KEY = "riverr_last_non_settings_path";
export const RIVERR_LAST_SETTINGS_TAB_KEY = "riverr_last_settings_tab";

export const SETTINGS_DEFAULT_TAB = "profile";

/**
 * IA for the Settings sidebar (NAV.2).
 *
 * Flat grouped list — every item is a leaf under a non-interactive
 * section label. Shape:
 *   group: `{ id, label, items: leaf[] }`
 *   leaf:  `{ id, label, path?, exact? }`
 *
 * Icons are attached at render time in `SettingsSidebar` (keyed by `id`).
 * Visibility is decided at render time, so this file stays pure data.
 *
 * Admin vs seller see different subsets via visibility gates — seller-
 * facing leaves stay in place; admin IA is the remodeled structure.
 */
export const SETTINGS_NAV = [
  {
    id: "account",
    label: "Account",
    items: [
      { id: "profile", label: "Profile" },
      { id: "shipping-address", label: "Shipping address" },
      { id: "locations", label: "Locations" },
      { id: "team-access/invite", label: "Team" },
      // Seller-facing team leaves (visibility-gated).
      { id: "team-access/share-access", label: "Share access" },
      { id: "team-access/invoices", label: "Invoices" },
      // Sellers upload their own certs (admins review under Tax → Resale certificates).
      { id: "certificates", label: "Certificates" },
    ],
  },
  {
    id: "organization",
    label: "Organization",
    items: [
      { id: "ecommerce-channels", label: "Ecommerce connections" },
      { id: "domains", label: "Domains" },
      // Sending, admin alerts, customer emails, and order messages.
      {
        id: "email",
        label: "Email",
        path: `${SETTINGS_PREFIX}/email`,
      },
      // Storefront contact form routing (hidden when storefronts are off).
      { id: "customer-support", label: "Customer support" },
      { id: "integrations", label: "Integrations" },
      { id: "announcement", label: "Announcement bar" },
      // Big Frog HQ only (visibility-gated): routing health of every
      // franchise store on the national store.
      { id: "store-status", label: "Store status" },
    ],
  },
  {
    // POS.1 — admin config for counter registers (day-to-day use stays /pos).
    id: "pos",
    label: "Register",
    items: [
      { id: "pos/stores", label: "Stores" },
    ],
  },
  {
    id: "catalog",
    label: "Catalog",
    items: [
      // Sellers / pro only — admins use Base products (main nav).
      { id: "catalog/blank-products", label: "Blank products" },
      // Seller-facing inserts (admin catalog lives under Manufacturing).
      { id: "catalog/inserts-and-tags", label: "Inserts and tags" },
      // Sellers and admins: saved per-size neck labels (admins see every seller).
      { id: "catalog/neck-labels", label: "Neck labels" },
      // Temporarily hidden via visibility gates.
      { id: "catalog/psd-bulk-import", label: "PSD bulk import" },
      { id: "catalog/mockup-marketplace", label: "Mockup marketplace" },
      { id: "catalog/psd-uploads", label: "PSD uploads" },
      // Admin: block overlapping print areas (Front vs Left Chest, etc.).
      { id: "catalog/placement-conflicts", label: "Placement conflicts" },
    ],
  },
  {
    // INV.2 — Warehouse and 3PL program are siblings.
    id: "inventory",
    label: "Inventory",
    items: [
      {
        id: "inventory/warehouse",
        label: "Warehouse",
        path: `${SETTINGS_PREFIX}/inventory/warehouse`,
      },
      {
        id: "inventory/handheld",
        label: "Handheld setup",
        path: `${SETTINGS_PREFIX}/inventory/handheld`,
      },
      {
        id: "inventory/3pl",
        label: "3PL program",
        path: `${SETTINGS_PREFIX}/inventory/3pl`,
      },
      {
        id: "inventory/stock-by-location",
        label: "Stock by location",
        path: `${SETTINGS_PREFIX}/inventory/stock-by-location`,
      },
      {
        id: "inventory/stock-planning",
        label: "Stock planning",
        path: `${SETTINGS_PREFIX}/inventory/stock-planning`,
      },
      {
        // Custom suppliers: order email, contact, lead time; POs are emailed.
        id: "inventory/suppliers",
        label: "Suppliers",
        path: `${SETTINGS_PREFIX}/inventory/suppliers`,
      },
      {
        id: "inventory/auto-ordering",
        label: "Auto-ordering",
        path: `${SETTINGS_PREFIX}/inventory/auto-ordering`,
      },
      {
        id: "inventory/supplier-sync",
        label: "Inventory sync",
        path: `${SETTINGS_PREFIX}/inventory/supplier-sync`,
      },
      {
        // INV.5 — seller stock sync (moved from the seller Inventory menu).
        id: "inventory/stock-sync",
        label: "Stock sync",
        path: "/inventory/sync-settings",
      },
    ],
  },
  {
    id: "shipping",
    label: "Shipping",
    items: [
      { id: "shipping/zones", label: "Shipping zones" },
      { id: "shipping/accounts", label: "Shipping accounts" },
      { id: "shipping/settings", label: "Shipping settings" },
      { id: "shipping/declared-value", label: "Declared value" },
      { id: "shipping/print-on-ship", label: "Print on ship" },
      { id: "shipping/popup-store", label: "Store shipping" },
    ],
  },
  {
    id: "pricing",
    label: "Pricing",
    items: [
      { id: "pricing/tables", label: "Pricing tables" },
      { id: "pricing/tiers", label: "Tiers" },
      ...(isBespokeThreadsDomain || isOmniCommerceDomain
        ? [{ id: "pricing/seller-subscriptions", label: "Seller subscriptions" }]
        : []),
      { id: "pricing/markup", label: "Markup" },
      { id: "pricing/quote-payments", label: "Quotes" },
      {
        id: "pricing/seller-quote-requests",
        label: "Seller quote requests",
      },
      // Admin: sellers apply before their orders are produced.
      // (No Sellers group exists yet; lives beside the other seller rules.)
      { id: "sellers/onboarding", label: "Seller onboarding" },
      { id: "pricing/currency", label: "Currency" },
      { id: "pricing/network-seller-fee", label: "Network seller fee" },
    ],
  },
  {
    id: "manufacturing",
    label: "Manufacturing",
    items: [
      {
        id: "machines",
        label: "Printers and machines",
        path: `${SETTINGS_PREFIX}/machines`,
      },
      { id: "manufacturing/batch-schedulers", label: "Batch schedulers" },
      { id: "manufacturing/batch-numbers", label: "Batch numbers" },
      { id: "manufacturing/order-changes", label: "Order changes" },
      { id: "manufacturing/barcode-labels", label: "Barcode labels" },
      { id: "manufacturing/batch-csv", label: "Batch CSV" },
      { id: "manufacturing/dtg-options", label: "DTG options" },
      { id: "manufacturing/order-status-colors", label: "Order status colors" },
      { id: "manufacturing/embroidery-options", label: "Embroidery options" },
      { id: "manufacturing/inserts-and-tags", label: "Inserts and tags" },
      // Admin: fulfillment ledger. Sellers keep `billing/billing` under Billing.
      { id: "fulfillment", label: "Fulfillment" },
    ],
  },
  {
    // Franchise admins only (empty groups drop out of the sidebar). Everything
    // here applies to the network's national store, never the franchisee's
    // own stores.
    id: "national-store",
    label: "National store",
    items: [{ id: "availability", label: "Availability" }],
  },
  {
    id: "billing",
    label: "Billing",
    items: [
      { id: "billing", label: "Billing", exact: true },
      {
        id: "billing/seller-subscription",
        label: "Subscription",
        path: "/subscription",
      },
      { id: "billing/production-invoices", label: "Production invoices" },
      { id: "billing/ai-credits", label: "AI credits" },
      { id: "billing/payouts", label: "Popup store payouts" },
      // Seller-only Fulfillment + Payouts (admins use Manufacturing → Fulfillment).
      { id: "billing/billing", label: "Fulfillment" },
      { id: "billing/payments", label: "Payouts" },
    ],
  },
  {
    id: "branding",
    label: "Branding",
    items: [
      { id: "branding/business", label: "Business branding" },
      // Redirects to Organization → Email → Admin alerts.
      { id: "branding/email-template", label: "Admin alerts" },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    items: [
      { id: "platform/enterprises", label: "Enterprises" },
      { id: "platform/admin-panel", label: "Admin panel" },
    ],
  },
  {
    id: "developer",
    label: "Developer",
    items: [
      { id: "api-keys", label: "API credentials" },
      { id: "webhooks", label: "Webhooks" },
    ],
  },
];

/** Canonical list of every leaf id reachable from the Settings rail. */
export const SETTINGS_LEAF_IDS = SETTINGS_NAV.flatMap((g) =>
  g.items.filter((item) => !item.path).map((item) => item.id)
);

export function isValidSettingsTab(id) {
  if (!id) return false;
  return SETTINGS_LEAF_IDS.includes(id);
}

export function settingsPathFor(leafId) {
  return `${SETTINGS_PREFIX}/${leafId}`;
}

/** Resolve sidebar destination — some leaves link outside `/settings/...`. */
export function navItemPath(item) {
  if (item?.path) return item.path;
  return settingsPathFor(item?.id || item);
}
